import { collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp, writeBatch, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { Category, NewCategory, CategoryCustomizationRules } from '../models/Category';
import productService from './productService';
import imageOptimizationService from './imageOptimizationService';
import { adminScopeService } from './adminScopeService';

// Collection Firestore
const CATEGORIES_COLLECTION = 'categories'; // Nom de la collection dans Firestore
const CUSTOMIZATION_RULES_COLLECTION = 'customizationRules';

// Conversion des données Firestore en modèle Category
const convertFirestoreDataToCategory = (id: string, data: any): Category => {
  const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined;
  const updatedAt = data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined;
  
  return {
    id,
    storeId: data.storeId,
    name: data.name || '',
    description: data.description || '',
    imageUrl: data.imageUrl || '',
    imageVersion: data.imageVersion || undefined,
    // Images optimisées pour l'app client
    thumbnailUrl: data.thumbnailUrl || null,
    mediumImageUrl: data.mediumImageUrl || null,
    highImageUrl: data.highImageUrl || null,
    order: data.displayOrder ?? data.order ?? 0,
    isActive: data.isActive ?? true,
    isVisibleOnline: data.isVisibleOnline ?? (!((data.isAddOn ?? data.isAddonCategory) ?? false) && (data.isActive ?? true)),
    isVisiblePos: data.isVisiblePos ?? true,
    // Mapper le champ Firestore isAddOn vers isAddonCategory
    isAddonCategory: data.isAddOn ?? data.isAddonCategory ?? false,
    isPromotionCategory: data.isPromotionCategory ?? id === 'promotions',
    createdAt,
    updatedAt
  };
};

const normalizeCategoryKey = (value: string): string => value.toLowerCase().trim();

const getRequiredCurrentStoreId = (action: string): string => {
  const storeId = adminScopeService.getCurrentStoreId();
  if (!storeId) {
    throw new Error(`Aucun magasin actif sélectionné pour ${action}.`);
  }
  return storeId;
};

const categoryMatchesCurrentStoreScope = (category: Category): boolean => {
  const currentStoreId = adminScopeService.getCurrentStoreId();
  if (!currentStoreId) {
    return true;
  }
  return category.storeId === currentStoreId;
};

const findStoreCategoryByKey = async (
  storeId: string,
  categoryKey: string
): Promise<Category | null> => {
  const categoriesRef = collection(db, CATEGORIES_COLLECTION);
  const snapshot = await getDocs(
    query(
      categoriesRef,
      where('storeId', '==', storeId),
      where('categoryKey', '==', categoryKey)
    )
  );

  const match = snapshot.docs[0];
  return match ? convertFirestoreDataToCategory(match.id, match.data()) : null;
};

const assertCategoryInCurrentStoreScope = (
  category: Category | null,
  id: string,
  action: string
): Category => {
  if (!category) {
    throw new Error(`Catégorie ${id} introuvable pour ${action}.`);
  }

  if (!categoryMatchesCurrentStoreScope(category)) {
    throw new Error(`La catégorie ${id} est hors du scope magasin actif.`);
  }

  return category;
};

const resolveCategoryCreateDocId = async (
  storeId: string,
  categoryKey: string
): Promise<string> => {
  const sameStoreCategory = await findStoreCategoryByKey(storeId, categoryKey);
  if (sameStoreCategory) {
    throw new Error(
      `Une catégorie "${sameStoreCategory.name}" existe déjà dans ce magasin.`
    );
  }

  const legacyRef = doc(db, CATEGORIES_COLLECTION, categoryKey);
  const legacySnap = await getDoc(legacyRef);

  if (!legacySnap.exists()) {
    return categoryKey;
  }

  const legacyCategory = convertFirestoreDataToCategory(
    legacySnap.id,
    legacySnap.data()
  );

  if (legacyCategory.storeId === storeId) {
    throw new Error(
      `Une catégorie "${legacyCategory.name}" existe déjà dans ce magasin.`
    );
  }

  throw new Error(
    `Une catégorie portant cette clé existe déjà sur un autre magasin. Utilise d'abord la catégorie clonée du magasin ou renomme-la avant création.`
  );
};

const buildScopedCustomizationRulesDocId = (storeId: string, categoryId: string): string =>
  `${storeId}__${categoryId}`;

const findScopedCustomizationRulesDocId = async (
  storeId: string,
  categoryId: string
): Promise<string | null> => {
  const rulesRef = collection(db, CUSTOMIZATION_RULES_COLLECTION);
  const scopedSnapshot = await getDocs(
    query(
      rulesRef,
      where('storeId', '==', storeId),
      where('categoryId', '==', categoryId)
    )
  );

  if (!scopedSnapshot.empty) {
    return scopedSnapshot.docs[0].id;
  }

  const predictedDocId = buildScopedCustomizationRulesDocId(storeId, categoryId);
  const predictedSnap = await getDoc(doc(db, CUSTOMIZATION_RULES_COLLECTION, predictedDocId));
  return predictedSnap.exists() ? predictedDocId : null;
};

// Service pour la gestion des catégories
const categoryService = {
  // Récupérer toutes les catégories
  getCategories: async (options?: { storeId?: string | null }): Promise<Category[]> => {
    try {
      const categoriesRef = collection(db, CATEGORIES_COLLECTION);
      const hasExplicitStoreFilter = options
        ? Object.prototype.hasOwnProperty.call(options, 'storeId')
        : false;
      const currentStoreId = hasExplicitStoreFilter
        ? options?.storeId ?? null
        : adminScopeService.getCurrentStoreId();
      const querySnapshot = currentStoreId
        ? await getDocs(query(categoriesRef, where('storeId', '==', currentStoreId)))
        : await getDocs(categoriesRef);

      let categories = querySnapshot.docs.map((categoryDoc) =>
        convertFirestoreDataToCategory(categoryDoc.id, categoryDoc.data())
      );

      if (currentStoreId) {
        categories = categories.filter((category) => category.storeId === currentStoreId);
      }

      categories.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return a.name.localeCompare(b.name);
      });

      return categories;
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      throw error;
    }
  },

  // Récupérer une catégorie par son ID
  getCategoryById: async (id: string): Promise<Category | null> => {
    try {
      const docRef = doc(db, CATEGORIES_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      const category = convertFirestoreDataToCategory(docSnap.id, docSnap.data());
      if (!categoryMatchesCurrentStoreScope(category)) {
        return null;
      }

      return category;
    } catch (error) {
      console.error(`Erreur lors de la récupération de la catégorie ${id}:`, error);
      throw error;
    }
  },

  // Créer une nouvelle catégorie
  createCategory: async (category: NewCategory, imageFile?: File): Promise<Category> => {
    try {
      let order = 0;
      const storeId = getRequiredCurrentStoreId('créer une catégorie');
      const categoryKey = normalizeCategoryKey(category.name);

      try {
        const existingCategories = await categoryService.getCategories({ storeId });
        if (existingCategories.length > 0) {
          const maxOrder = Math.max(...existingCategories.map(cat => cat.order || 0));
          order = maxOrder + 1;
        }
      } catch (orderError) {
        console.error('Erreur lors de la détermination de l\'ordre de la catégorie:', orderError);
      }
      const isAddonCategory = category.isAddonCategory ?? false;
      const isActive = category.isActive ?? true;
      const isVisibleOnline = category.isVisibleOnline ?? (!isAddonCategory && isActive);
      const isVisiblePos = category.isVisiblePos ?? true;
      const docId = await resolveCategoryCreateDocId(storeId, categoryKey);
      const newCategoryData: any = {
        name: category.name,
        description: category.description || '',
        imageUrl: category.imageUrl || '',
        storeId,
        categoryKey,
        sourceStoreId: storeId,
        sourceCategoryId: categoryKey,
        order,
        displayOrder: order,
        isActive,
        isVisibleOnline,
        isVisiblePos,
        isPromotionCategory: category.isPromotionCategory ?? categoryKey === 'promotions',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isAddOn: isAddonCategory,
        isAddonCategory,
      };

      const docRef = doc(db, CATEGORIES_COLLECTION, docId);
      await setDoc(docRef, newCategoryData);

      if (imageFile) {
        const imageVersion = Date.now();
        const imageUrl = await categoryService.uploadCategoryImage(
          docRef.id,
          imageFile,
          category.name,
          imageVersion
        );
        
        // Mettre à jour la catégorie avec l'URL de l'image
        await updateDoc(docRef, { imageUrl, imageVersion });
        newCategoryData.imageUrl = imageUrl;
        newCategoryData.imageVersion = imageVersion;
      }

      return {
        id: docRef.id,
        ...newCategoryData,
        createdAt: new Date(),
        updatedAt: new Date()
      } as Category;
    } catch (error) {
      console.error('Erreur lors de la création de la catégorie:', error);
      throw error;
    }
  },

  // Mettre à jour une catégorie existante
  updateCategory: async (id: string, categoryData: Partial<Category>, imageFile?: File): Promise<Category> => {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
      const currentCategory = assertCategoryInCurrentStoreScope(
        await categoryService.getCategoryById(id),
        id,
        'mise à jour'
      );

      const nameChanged = categoryData.name && categoryData.name !== currentCategory.name;
      if (nameChanged) {
        console.log(`Changement de nom détecté: "${currentCategory.name}" -> "${categoryData.name}"`);
        try {
          await productService.updateProductsCategory(currentCategory.name, categoryData.name!);
          console.log(`Synchronisation des produits terminée pour la catégorie: ${categoryData.name}`);
        } catch (productError) {
          console.error('Erreur lors de la synchronisation des produits:', productError);
          const errorMessage = productError instanceof Error ? productError.message : 'Erreur inconnue';
          throw new Error(`Erreur lors de la synchronisation des produits: ${errorMessage}`);
        }
      }

      const updateData: any = {
        updatedAt: serverTimestamp(),
      };

      if (categoryData.name !== undefined) updateData.name = categoryData.name;
      if (categoryData.description !== undefined) updateData.description = categoryData.description;
      if (categoryData.order !== undefined) {
        updateData.order = categoryData.order;
        updateData.displayOrder = categoryData.order;
      }
      if (categoryData.isActive !== undefined) updateData.isActive = categoryData.isActive;
      if (categoryData.isVisibleOnline !== undefined) updateData.isVisibleOnline = categoryData.isVisibleOnline;
      if (categoryData.isVisiblePos !== undefined) updateData.isVisiblePos = categoryData.isVisiblePos;
      if (categoryData.isAddonCategory !== undefined) {
        updateData.isAddonCategory = categoryData.isAddonCategory;
        updateData.isAddOn = categoryData.isAddonCategory;
      }
      if (categoryData.isPromotionCategory !== undefined) {
        updateData.isPromotionCategory = categoryData.isPromotionCategory;
      }

      if (imageFile) {
        const categoryName = updateData.name || currentCategory.name;
        const imageVersion = Date.now();
        const imageUrl = await categoryService.uploadCategoryImage(
          id,
          imageFile,
          categoryName,
          imageVersion
        );
        updateData.imageUrl = imageUrl;
        updateData.imageVersion = imageVersion;
      }

      await updateDoc(categoryRef, updateData);

      const updatedCategory = await categoryService.getCategoryById(id);
      if (!updatedCategory) {
        throw new Error(`Catégorie ${id} non trouvée après mise à jour`);
      }
      
      return updatedCategory;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la catégorie ${id}:`, error);
      throw error;
    }
  },

  // Supprimer une catégorie
  deleteCategory: async (id: string): Promise<void> => {
    try {
      // 1. Récupérer la catégorie pour vérifier s'il y a une image à supprimer
      const category = assertCategoryInCurrentStoreScope(
        await categoryService.getCategoryById(id),
        id,
        'suppression'
      );
      
      // 2. Supprimer la catégorie de Firestore
      await deleteDoc(doc(db, CATEGORIES_COLLECTION, id));
      
      // 3. Si la catégorie avait une image, la supprimer du stockage
      if (category?.imageUrl) {
        try {
          // Extraire le nom du fichier de l'URL
          const imageRef = ref(storage, `categories/${id}`);
          await deleteObject(imageRef);
        } catch (imageError) {
          console.error(`Erreur lors de la suppression de l'image de la catégorie ${id}:`, imageError);
          // On continue même si la suppression de l'image échoue
        }
      }
    } catch (error) {
      console.error(`Erreur lors de la suppression de la catégorie ${id}:`, error);
      throw error;
    }
  },

  // Télécharger une image de catégorie avec optimisation automatique
  uploadCategoryImage: async (
    categoryId: string,
    imageFile: File,
    categoryName?: string,
    imageVersion?: number
  ): Promise<string> => {
    try {
      console.log(`🚀 Upload image catégorie ${categoryId} avec optimisation...`);
      const safeImageVersion = imageVersion || Date.now();
      const originalExt = (imageFile.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
      
      // 1. Upload de l'image originale (pour compatibilité)
      const storageRef = ref(storage, `categories/${categoryId}/original_${safeImageVersion}.${originalExt}`);
      await uploadBytes(storageRef, imageFile);
      const originalImageUrl = await getDownloadURL(storageRef);
      
      // 2. Générer les versions optimisées si le nom de la catégorie est fourni
      if (categoryName) {
        try {
          console.log(`🔧 Génération des versions optimisées pour: ${categoryName}`);
          
          const optimizedUrls = await imageOptimizationService.optimizeCategoryImage(
            categoryId,
            imageFile,
            categoryName,
            { quality: 0.8, format: 'webp' }
          );
          
          // 3. Mettre à jour Firestore avec les URLs optimisées
          const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
          await updateDoc(categoryRef, {
            thumbnailUrl: optimizedUrls.thumbnailUrl,
            mediumImageUrl: optimizedUrls.mediumImageUrl,
            highImageUrl: optimizedUrls.highImageUrl,
            imageVersion: safeImageVersion,
            optimizedAt: serverTimestamp()
          });
          
          console.log(`✅ Optimisation terminée pour: ${categoryName}`);
          console.log(`📐 URLs générées:`, {
            thumbnail: optimizedUrls.thumbnailUrl.length + ' chars',
            medium: optimizedUrls.mediumImageUrl.length + ' chars',
            high: optimizedUrls.highImageUrl.length + ' chars'
          });
          
        } catch (optimizationError) {
          console.warn(`⚠️ Erreur optimisation pour ${categoryName}:`, optimizationError);
          // Continuer avec l'image originale en cas d'erreur d'optimisation
        }
      }
      
      return originalImageUrl;
    } catch (error) {
      console.error(`Erreur lors du téléchargement de l'image pour la catégorie ${categoryId}:`, error);
      throw error;
    }
  },

  // Récupérer les règles de customisation pour une catégorie
  getCategoryCustomizationRules: async (category: string): Promise<CategoryCustomizationRules | null> => {
    try {
      const currentStoreId = getRequiredCurrentStoreId(
        'la lecture des règles de customisation'
      );
      const rulesDocId = await findScopedCustomizationRulesDocId(
        currentStoreId,
        category
      );
      if (!rulesDocId) {
        return null;
      }
      const rulesRef = doc(db, CUSTOMIZATION_RULES_COLLECTION, rulesDocId);
      const rulesSnap = await getDoc(rulesRef);
      
      if (!rulesSnap.exists()) {
        return null;
      }
      
      return {
        category,
        storeId: rulesSnap.data().storeId,
        sections: rulesSnap.data().sections || []
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération des règles de customisation pour la catégorie ${category}:`, error);
      throw error;
    }
  },

  // Mettre à jour les règles de customisation d'une catégorie
  updateCategoryCustomizationRules: async (rules: CategoryCustomizationRules): Promise<void> => {
    try {
      const currentStoreId = getRequiredCurrentStoreId(
        'la mise à jour des règles de customisation'
      );
      const rulesDocId =
        (await findScopedCustomizationRulesDocId(currentStoreId, rules.category)) ??
        buildScopedCustomizationRulesDocId(currentStoreId, rules.category);
      const rulesRef = doc(db, CUSTOMIZATION_RULES_COLLECTION, rulesDocId);
      
      await setDoc(rulesRef, {
        storeId: currentStoreId,
        categoryId: rules.category,
        sections: rules.sections,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error(`Erreur lors de la mise à jour des règles de customisation pour la catégorie ${rules.category}:`, error);
      throw error;
    }
  },

  // Mettre à jour l'ordre d'affichage d'une catégorie
  updateCategoryOrder: async (id: string, newOrder: number): Promise<void> => {
    try {
      assertCategoryInCurrentStoreScope(
        await categoryService.getCategoryById(id),
        id,
        'mise à jour de l’ordre'
      );
      const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
      await updateDoc(categoryRef, {
        order: newOrder,
        displayOrder: newOrder,
        updatedAt: serverTimestamp()
      });
      console.log(`Ordre de la catégorie ${id} mis à jour: ${newOrder}`);
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'ordre de la catégorie ${id}:`, error);
      throw error;
    }
  },

  // Réorganiser l'ordre des catégories après un glisser-déposer
  reorderCategories: async (reorderedCategories: {id: string, order: number}[]): Promise<void> => {
    try {
      await Promise.all(
        reorderedCategories.map(({ id }) =>
          (async () => {
            assertCategoryInCurrentStoreScope(
              await categoryService.getCategoryById(id),
              id,
              'réorganisation'
            );
          })()
        )
      );

      // Utiliser une transaction batch pour mettre à jour toutes les catégories en une seule opération
      const batch = writeBatch(db);
      
      reorderedCategories.forEach(({id, order}) => {
        const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
        batch.update(categoryRef, {
          order,
          displayOrder: order,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      console.log(`Réorganisation de ${reorderedCategories.length} catégories terminée`);
    } catch (error) {
      console.error('Erreur lors de la réorganisation des catégories:', error);
      throw error;
    }
  }
};

export default categoryService;
